import { FEATURE } from "@/features";
import { Button } from "@components/ui/button";
import { Feature } from "@components/Feature";
import {
  Item,
  ItemMedia,
  ItemContent,
  ItemTitle,
  ItemActions,
} from "@components/ui/item";
import { Trans, useLingui } from "@lingui/react/macro";
import { MessageSquareIcon } from "lucide-react";
import { SEARCH_KIND, FILTER } from "@/constants";
import { formatDateForApi } from "@/utils";
import { getMentionViewDateRanges } from "@/views/MentionsView/MentionLayout/ranges";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { SearchLight } from "@/types/Search";
import { SearchFilterV4 } from "@/types/SearchFilter";
import { postTrackingData } from "@/api/endpoints/track";
import { EVENT } from "@/tracking_events";
import { useMutation } from "@tanstack/react-query";
import {
  requestAsyncExport,
  ASYNC_EXPORT_FORMAT,
  AsyncExportFormat,
} from "@/api/endpoints/async-export";
import { toast } from "sonner";
import { Spinner } from "@components/ui/spinner";
import { useDelayedLoading } from "@/hooks/useDelayedLoading";

export default function Mentions({
  filters,
  search,
  onClose,
}: {
  search: Nullable<SearchLight> | undefined;
  filters: SearchFilterV4;
  onClose?: () => void;
}) {
  const { t } = useLingui();
  const { orgUuid } = useParams<{ orgUuid: string }>();

  const resolvedFilters = useMemo(() => {
    let defaults = {};

    if (search?.kind !== SEARCH_KIND.SAVED) {
      const defaultRange = getMentionViewDateRanges().find(
        (range) => range.default
      );
      if (defaultRange) {
        defaults = {
          [FILTER.START_DATE]: defaultRange.startDate
            ? formatDateForApi(defaultRange.startDate)
            : null,
          [FILTER.END_DATE]: defaultRange.endDate
            ? formatDateForApi(defaultRange.endDate)
            : null,
        };
      }
    }

    return { ...defaults, ...filters };
  }, [filters, search]);

  const { mutate: startAsyncExport, isPending } = useMutation({
    mutationFn: (format: AsyncExportFormat) => {
      if (!orgUuid) throw new Error("Missing orgUuid");

      const trackEvent =
        format === ASYNC_EXPORT_FORMAT.EXCEL
          ? EVENT.EXPORT_MENTIONS_TO_EXCEL
          : EVENT.EXPORT_MENTIONS_TO_CSV;
      postTrackingData(trackEvent);

      return requestAsyncExport({
        format,
        org_uuid: orgUuid,
        search_uuid: search?.uuid,
        filters: resolvedFilters,
      });
    },
    onSuccess: (res) => {
      if (!res.ok) throw new Error();

      toast.success(
        t`Exporten har startats. Du får ett mejl med en nedladdningslänk när den är klar.`
      );
      onClose?.();
    },
    onError: () => {
      toast.error(t`Kunde inte starta exporten. Försök igen.`);
    },
  });

  const { isVisible: showSpinner } = useDelayedLoading(isPending);

  return (
    <Feature required={FEATURE.HAS_EXCEL_EXPORT_FEATURE}>
      {search && (
        <Item variant="outline" size="sm">
          <ItemMedia variant="icon">
            <MessageSquareIcon />
          </ItemMedia>
          <ItemContent>
            <ItemTitle>
              <Trans>Omnämnanden</Trans>
            </ItemTitle>
          </ItemContent>
          <ItemActions>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={isPending}
              data-testid="excel-export-link"
              onClick={() => startAsyncExport(ASYNC_EXPORT_FORMAT.EXCEL)}
            >
              <Trans>Exportera till Excel</Trans>
              {showSpinner && <Spinner />}
            </Button>

            <Button
              type="button"
              size="sm"
              disabled={isPending}
              data-testid="csv-export-link"
              onClick={() => startAsyncExport(ASYNC_EXPORT_FORMAT.CSV)}
            >
              <Trans>Exportera till CSV</Trans>
              {showSpinner && <Spinner />}
            </Button>
          </ItemActions>
        </Item>
      )}
    </Feature>
  );
}
